// WbTicketHeader — Phase C10 Group 1.
//
// Top strip for the New Ticket page. Shows:
//   • Title (ticket number if POST has landed, "New ticket (draft)" if the
//     Start modal has staged a pendingShell, "—" otherwise).
//   • Status / direction / weighing-mode badges sourced from the ticket or
//     the staged shell.
//   • Autosave tri-state indicator ("Saving…" / "Saved HH:MM" /
//     "⚠ Save failed — retry"). Test hook: data-testid="wb-autosave-indicator".
//   • Abandon button — confirm-prompts when a ticket exists (so the
//     operator knows it'll leave a draft on the server); skips the
//     confirm when nothing has been POSTed yet.

import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../../components/ui/PageHeader/PageHeader';
import { Badge, type BadgeVariant } from '../../../../components/ui/Badge/Badge';
import { Button } from '../../../../components/ui/Button/Button';
import { useWbTicket } from '../../context/WbTicketContext';
import type {
  TicketDirection,
  TicketStatus,
  WeighingMode,
} from '../../hooks/useWbTicket';

const STATUS_LABEL: Record<TicketStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  completed: 'Completed',
  voided: 'Voided',
};

const STATUS_VARIANT: Record<TicketStatus, BadgeVariant> = {
  draft: 'muted',
  open: 'info',
  completed: 'success',
  voided: 'danger',
};

const DIRECTION_LABEL: Record<TicketDirection, string> = {
  inwards: 'Inwards',
  outwards: 'Outwards',
};

const WEIGHING_MODE_LABEL: Record<WeighingMode, string> = {
  two_pass: 'Two-pass',
  stored_tare: 'Stored tare',
  manual_tare: 'Manual tare',
  units_only: 'Units only',
};

function fmtTime(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function WbTicketHeader(): JSX.Element {
  const navigate = useNavigate();
  const {
    ticket,
    pendingShell,
    isSaving,
    saveError,
    lastSavedAt,
  } = useWbTicket();

  // Title resolution — ticket_number wins when the POST has landed (and
  // the server has assigned one). Before any POST we render the
  // explicit "(draft)" suffix so the operator knows the row isn't on
  // the server yet.
  const title = ticket?.ticket_number
    ? ticket.ticket_number
    : pendingShell !== null || ticket !== null
      ? 'New ticket (draft)'
      : '—';

  // Status: real ticket → server's status; pending shell only → muted
  // "Draft" badge (matches the "draft" status visually but explicit
  // about the no-POST-yet state).
  const status: TicketStatus | null = ticket?.status ?? null;
  const direction: TicketDirection | null =
    ticket?.direction ?? pendingShell?.direction ?? null;
  const weighingMode: WeighingMode | null =
    ticket?.weighing_mode ?? pendingShell?.weighing_mode ?? null;

  // Autosave indicator — tri-state. Order matters: saveError beats
  // isSaving (a retried save that failed should NOT show "Saving…"
  // afterwards), and the "Saved …" line only appears once we have a
  // lastSavedAt with no live error.
  let autosaveContent: JSX.Element;
  if (saveError !== null) {
    autosaveContent = (
      <span style={{ color: 'var(--color-status-unpaid_text, #991b1b)' }}>
        ⚠ Save failed — retry
      </span>
    );
  } else if (isSaving) {
    autosaveContent = <span>Saving…</span>;
  } else if (lastSavedAt !== null) {
    autosaveContent = (
      <span style={{ color: 'var(--color-text-muted, #64748b)' }}>
        Saved {fmtTime(lastSavedAt)}
      </span>
    );
  } else {
    autosaveContent = <span />;
  }

  function handleAbandon() {
    // Navigate away directly — the editor's unmount cleanup releases the lock
    // (migration 167) and autosave has already persisted edits. No confirm.
    navigate('/weighbridge/tickets');
  }

  const subtitle = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        flexWrap: 'wrap',
      }}
    >
      {status !== null ? (
        <Badge variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</Badge>
      ) : (
        <Badge variant="muted">Draft</Badge>
      )}
      {direction !== null ? (
        <Badge variant="default">{DIRECTION_LABEL[direction]}</Badge>
      ) : null}
      {weighingMode !== null ? (
        <Badge variant="muted">{WEIGHING_MODE_LABEL[weighingMode]}</Badge>
      ) : null}
      <span data-testid="wb-autosave-indicator" style={{ fontSize: '0.8125rem' }}>
        {autosaveContent}
      </span>
    </span>
  );

  return (
    <PageHeader
      title={title}
      subtitle={subtitle}
      actions={
        <Button
          variant="secondary"
          onClick={handleAbandon}
          title="Abandon ticket"
        >
          Abandon ticket
        </Button>
      }
    />
  );
}
